import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Waves } from "lucide-react";
import { Section, Overline } from "../components/Shared";
import { submitLead } from "../lib/api";
import { CONTACT } from "../lib/assets";
import { Seo } from "../components/Seo";

const CRUMBS = [["Request a Quote", "/request-a-quote"]];
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const WATERBODY_TYPES = [
  "Reservoir",
  "Community / Municipal Lake",
  "HOA / Association Lake",
  "Private Pond",
  "Private Lake",
  "Other",
];

const GOALS = [
  "Depth / Bathymetric Mapping",
  "Aquatic Vegetation Mapping",
  "Bottom Hardness / Sediment",
  "Fish & Habitat Mapping",
  "Side-Scan Imagery",
  "GIS Map Services",
  "Not sure yet",
];

const initial = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  waterbody_type: "",
  region: "",
  surface_acres: "",
  goal: "",
  message: "",
};

export default function RequestQuote() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => ({ ...initial, region: params.get("region") || "" }));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setVal = (key) => (val) => setForm({ ...form, [key]: val });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.waterbody_type || !form.region) {
      setError("Please fill in your name, email, waterbody type and location.");
      return;
    }
    setSubmitting(true);
    try {
      const lead = await submitLead(form);
      setResult(lead);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Section className="pt-36 min-h-[70vh] flex items-center">
        <Seo page="quote" breadcrumbs={CRUMBS} />
        <div className="max-w-xl mx-auto text-center" data-testid="quote-confirmation">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-cyan-50 border border-cyan-200 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-cyan-700" />
          </div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Request received</h1>
          <p className="mt-4 text-slate-700 text-lg leading-relaxed">
            Thank you, {result.name.split(" ")[0]}. Your quote request has been logged and our team will reach out
            shortly to plan your survey.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white shadow-sm px-5 py-3">
            <span className="text-base text-slate-600">Reference:</span>
            <span className="font-mono text-cyan-700 font-semibold" data-testid="quote-reference">{result.reference}</span>
          </div>
          <div className="mt-8">
            <button
              data-testid="submit-another-button"
              onClick={() => {
                setResult(null);
                setForm(initial);
              }}
              className="text-cyan-700 hover:text-cyan-800 font-semibold"
            >
              Submit another request
            </button>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section className="pt-36">
      <Seo page="quote" breadcrumbs={CRUMBS} />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <Overline>Request a Quote</Overline>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
            Let's map your waterbody
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-700 leading-relaxed">
            Tell us a little about your lake, pond or reservoir and what you'd like to learn. We'll follow up with a
            tailored survey plan and quote. No obligation.
          </p>
          <div className="mt-8 space-y-4 text-slate-600">
            <div className="flex items-center gap-3">
              <Waves className="w-5 h-5 text-cyan-700" />
              <span>Serving {CONTACT.serviceArea}</span>
            </div>
            <ul className="space-y-2 text-base">
              {["We reply within one business day", "No obligation, no software to buy", "Our crew travels to your waterbody"].map((t) => (
                <li key={t} className="flex items-center gap-2.5"><CheckCircle2 className="w-4 h-4 text-cyan-600 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-3">
          <form
            onSubmit={handleSubmit}
            data-testid="quote-form"
            className="rounded-2xl border border-slate-200 bg-white shadow-[0_10px_40px_rgba(15,23,42,0.06)] p-6 sm:p-8 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name *">
                <Input data-testid="quote-input-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="bg-white border-slate-300 text-slate-900 h-11 text-base" />
              </Field>
              <Field label="Organization / Property">
                <Input data-testid="quote-input-organization" value={form.organization} onChange={set("organization")} placeholder="Water District / HOA / Home" className="bg-white border-slate-300 text-slate-900 h-11 text-base" />
              </Field>
              <Field label="Email *">
                <Input data-testid="quote-input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="bg-white border-slate-300 text-slate-900 h-11 text-base" />
              </Field>
              <Field label="Phone">
                <Input data-testid="quote-input-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className="bg-white border-slate-300 text-slate-900 h-11 text-base" />
              </Field>
              <Field label="Waterbody type *">
                <Select value={form.waterbody_type} onValueChange={setVal("waterbody_type")}>
                  <SelectTrigger data-testid="quote-select-waterbody" className="bg-white border-slate-300 text-slate-900 h-11 text-base">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {WATERBODY_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`waterbody-option-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Location / region *">
                <Input data-testid="quote-input-region" value={form.region} onChange={set("region")} placeholder="City, State/Province" className="bg-white border-slate-300 text-slate-900 h-11 text-base" />
              </Field>
              <Field label="Approx. surface acres">
                <Input data-testid="quote-input-acres" value={form.surface_acres} onChange={set("surface_acres")} placeholder="e.g. 45" className="bg-white border-slate-300 text-slate-900 h-11 text-base" />
              </Field>
              <Field label="Primary goal">
                <Select value={form.goal} onValueChange={setVal("goal")}>
                  <SelectTrigger data-testid="quote-select-goal" className="bg-white border-slate-300 text-slate-900 h-11 text-base">
                    <SelectValue placeholder="Select goal" />
                  </SelectTrigger>
                  <SelectContent>
                    {GOALS.map((g) => (
                      <SelectItem key={g} value={g} data-testid={`goal-option-${g}`}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Tell us more">
              <Textarea
                data-testid="quote-input-message"
                value={form.message}
                onChange={set("message")}
                placeholder="Describe your waterbody, timeline, and what you'd like to achieve."
                rows={4}
                className="bg-white border-slate-300 text-slate-900 text-base"
              />
            </Field>

            {error && (
              <p data-testid="quote-error" className="text-base text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="quote-form-submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-white font-bold px-6 py-3.5 rounded-xl shadow-lg shadow-cyan-600/25 transition-all disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                </>
              ) : (
                "Submit Request"
              )}
            </button>
          </form>
        </div>
      </div>
    </Section>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-slate-800 text-base font-medium">{label}</Label>
    {children}
  </div>
);
